// ─────────────────────────────────────────────
//  useSwapPair — the two people in a room trade places.
//
//  Owner, 2026-09-10: *"we can swap now, but i want to have a way to change the
//  names and the position directly fron the box"*. The sheet writes "A orient B"
//  and is sometimes the wrong way round; this is the correction, made from the
//  ORIENTEES card without re-typing either name.
//
//  ⚠️ NOT A DEPARTURE. `useMovePreceptorOut` ends the pair — somebody leaves the
//  room and the second slot is cleared, which is why it stamps nothing but still
//  changes who covers what. A swap moves nobody out: both people are in the room
//  before and after, so there is no `freeSince` to stamp, no bench to write and
//  no room-doc mirror to keep in step. That is the entire reason this is the
//  cheap half of the edit and re-picking a person is not.
//
//  ⚠️ THE PATCH IS RE-DERIVED AT WRITE TIME, FROM THE LIVE ROW. Same rule as
//  `useMovePreceptorOut`: the card hands back a display row built on an earlier
//  render, and between that render and the click an import, the sheet or another
//  session may have changed who is in the room. Looking the assignment up again
//  and asking `swapPairPatch` fresh is what stops a stale card swapping two
//  people who are no longer there. It is also the second gate — the domain
//  refuses a pair whose second person matches no staff record, and the refusal
//  is shown rather than swallowed.
//
//  ⚠️ THE FIELD NAMES ARE NOT SPELLED HERE. `domain/secondProviderWrite` owns
//  them. A second place that knows which fields make up a seat is how a surface
//  starts writing a room nothing else reads.
// ─────────────────────────────────────────────
import { useCallback, useRef } from "react";
import { updateAssignmentRow } from "../../firebase/firestore";
import { swapPairPatch } from "../../domain/secondProviderWrite";

export default function useSwapPair({ assignments, toast, guard }) {
  // Held in refs so `commit` keeps ONE identity across renders. The page passes
  // an inline guard that closes over the live connection band, and listing it as
  // a dependency would mint a new `commit` on every render — which then
  // re-renders every bar on the chart. Same reason as `useMovePreceptorOut`.
  const guardRef = useRef(guard);
  guardRef.current = guard;
  const rowsRef = useRef(assignments);
  rowsRef.current = assignments;

  const commit = useCallback(
    async (cardRow) => {
      const id = cardRow?.assignmentId;
      if (!id) return false;
      if (guardRef.current && !guardRef.current()) return false;

      // ⚠️ EVERY CLAIMING CASE, NOT JUST THE ONE THE CARD NAMED. A room running
      // two cases today yields ONE card row (deduped per person-and-room), and a
      // future case still claims the room. Writing only the first left the rest
      // disagreeing, so the correction silently undid itself the moment the next
      // case started — no error, nothing on screen. The row carries them all.
      const ids = (Array.isArray(cardRow.assignmentIds) && cardRow.assignmentIds.length)
        ? cardRow.assignmentIds
        : [id];
      const targets = ids
        .map((assignmentId) => (rowsRef.current || []).find((a) => a?.id === assignmentId))
        .filter(Boolean);
      if (!targets.length) {
        if (toast) toast.error("That room has changed. Refresh and try again.");
        return false;
      }
      const live = targets[0];

      // ⚠️ THE DOMAIN GATE, ASKED AGAIN. `null` means the swap can no longer be
      // made — the second person lost their match, somebody already moved one of
      // them, or the seat is empty. Saying so beats a control that appears to do
      // nothing.
      const patch = swapPairPatch(live);
      if (!patch) {
        if (toast) {
          toast.error("These two cannot be swapped — the second name is not matched to a staff record.");
        }
        return false;
      }

      // Named from the PATCH, not from the card: the patch is what is about to
      // be written, so the sentence cannot describe a swap that did not happen.
      const takingRoom = String(patch.ahpName || "").trim() || "That person";
      const steppingDown = String(patch.orienteeName || "").trim() || "the other";
      const where = cardRow.room || "that room";
      try {
        // Each target re-derives its OWN patch: two cases in one room can differ
        // in shift code and leaving time, and one patch reused across them would
        // copy the first case's hours onto the second.
        for (const target of targets) {
          const p = target === live ? patch : swapPairPatch(target);
          if (p) await updateAssignmentRow(target.id, p);
        }
      } catch (err) {
        console.error("[Timeline] swap-pair write failed:", err);
        if (toast) toast.error("That change could not be saved. Please try again.");
        return false;
      }
      if (toast) {
        toast.success(`${takingRoom} now holds ${where}, with ${steppingDown}.`);
      }
      return true;
    },
    [toast]
  );

  return { commit };
}
